import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert } from 'reactstrap';
import { get } from 'lodash-es';
import { useDispatch } from 'react-redux';

import '../../index.scss';
import { setProcessorType } from '../../modules/invoice';
import MaintenanceAlert from './MaintenanceAlert';
import { isInvaluableSeller } from '../../utilities/BusinessLogic';
import { cfPath, isProd, reportsPath } from '../../utilities/UrlPaths';
import { useAxios } from '../../hooks/useAxios';

const Header = ({
  house: { houseName, houseRef, houseID, ingestType, disableINVAInvoicing } = {},
  user = {}
}) => {
  // (dyouberg) Comment out this and all references when maintenance window is over
  const [showMaintenanceAlert, setMaintenanceAlert] = useState(false);
  const { data: paymentProcessorTypeData } = useAxios(
    () => `/app/house/${houseRef}/paymentProcessorType`,
    [houseRef]
  );
  const dispatch = useDispatch();

  const isInvaluableAuctions = () => window.location.href.includes('invaluableauctions');
  // ability to show Looker link dynamically - but not now
  // const lookerHouseArray = isProd ? [4034, 22575, 22021] : [20, 4404, 1118];
  // const showLookerLink = lookerHouseArray.indexOf(houseID) > -1 && isInvaluableAuctions();
  const showLookerLink = isInvaluableAuctions();

  const hideMaintenanceBanner = () => {
    window.sessionStorage.setItem('hideMaintenance', true);
    setMaintenanceAlert(false);
  };

  const showMaintenanceBanner = () => {
    // If the user dismissed the alert - don't show it again this session
    if (window.sessionStorage.getItem('hideMaintenance')) {
      return;
    }

    const { pathname } = window.location;
    const maintenanceUrls = [
      '/my-auctions/current',
      '/my-auctions/past',
      '/session/live',
      '/session/timed'
    ];

    const showAlert = maintenanceUrls.some(url => pathname.indexOf(url) >= 0);
    setMaintenanceAlert(showAlert);
  };

  const {
    accessCalendar,
    accessCustomer,
    accessReports,
    accessSensitive,
    connectType,
    isInstant,
    isSuperUser,
    paymentProcessingAccountID,
    paymentProcessingEligible,
    paymentProcessingEnabled,
    userName
  } = user;

  const invalidUrl = '/?p=1';
  const { userAgent } = window.navigator;
  const isIE = userAgent.match(/Trident.*rv[ :]*11\./) || userAgent.indexOf('Edge') >= 0;
  const ycConnectTypes = ['s', 'p', 'e'];
  const showYourCustomers = ycConnectTypes.indexOf(connectType) > -1;

  const processorType = useMemo(() => get(paymentProcessorTypeData, 'data.data.processor-type'), [paymentProcessorTypeData]);

  useEffect(() => {
    dispatch(setProcessorType(get(paymentProcessorTypeData, 'data.data.processor-type')));
  }, [processorType]);

  const showPaymentCenterLink = paymentProcessingEligible && paymentProcessingEnabled;

  useEffect(() => {
    // Check to see whether or not to show the maintenance banner
    showMaintenanceBanner();

    // eslint-disable-next-line
    const _aaq = window._aaq || (window._aaq = []);

    const g = document.createElement('script');
    const s = document.getElementsByTagName('script')[0];
    g.type = 'text/javascript';
    g.defer = true;
    g.async = true;
    g.src = `${document.location.protocol}//cdn.evgnet.com/beacon/invaluable/invaluableauctions/scripts/evergage.min.js`;
    s.parentNode.insertBefore(g, s);
  }, []);

  /*
   * Oct 5th 2020 2:30 AM EDT is the end time for maintenance window.
   * Note EDT is 4 hours less than UTC time
   *
   * new Date(Date.UTC(2020, 9, 5, 6, 30, 0, 0))
   * Mon Oct 05 2020 02:30:00 GMT-0400 (Eastern Daylight Time)
   */
  const maintenanceEnd = Date.UTC(2020, 9, 5, 6, 30, 0, 0);
  const maintenanceWindowString =
    'Monday, October 5, 2020 at 2:00am EDT to Monday, October 5, 2020 at 2:30am EDT (October 5 from 6:00am to 6:30am UTC).';

  const auctionCalendarLabel = !isInvaluableSeller ? 'Auctions / Sales' : 'Sales';
  let auctionCalendarLink = <a href={invalidUrl}>{auctionCalendarLabel}</a>;
  const isRFCHouse = ingestType === 'r';
  if (accessCalendar === 1 && !isRFCHouse) {
    auctionCalendarLink = <Link to="/my-auctions/current">{auctionCalendarLabel}</Link>;
  } else if (accessCalendar === 1 && isRFCHouse) {
    auctionCalendarLink = (
      <a href={`${cfPath()}/al/auctionData/viewRfcGroups.cfm`}>{auctionCalendarLabel}</a>
    );
  }

  const getPaymentCenterLink = () => {
    switch (processorType) {
      case 'w': {
        const environmentUrl = isProd ? 'https://wepay.com' : 'https://stage.wepay.com';

        return `${environmentUrl}/account/${paymentProcessingAccountID}`;
      }
      case 'p':
        return 'https://payments.invaluableauctions.com';
      case 's':
        return 'https://connect.stripe.com';
      default:
        return invalidUrl;
    }
  };

  return (
    <header id="fullHeader">
      {isIE && (
        <Alert color="warning" className="mb-3">
          <strong>Improve your Experience: </strong>Switch to{' '}
          <a href="https://www.google.com/chrome/browser/">Google Chrome</a> or{' '}
          <a href="https://www.mozilla.org/firefox">Firefox</a> for the best on-site experience.
        </Alert>
      )}

      {/* This alert is only shown during maintenance windows */}
      {new Date() < maintenanceEnd && (
        <MaintenanceAlert
          hideMaintenanceBanner={hideMaintenanceBanner}
          maintenanceWindowString={maintenanceWindowString}
          showMaintenanceAlert={showMaintenanceAlert}
        />
      )}

      <div id="header">
        <a id="logo" href={`${cfPath()}/al/index.cfm`}>
          <img
            className="logo"
            src="https://www.invaluableauctions.com/images/af-inv-az.png"
            alt="InvaluableAuctions"
            width="525px"
          />
        </a>
        <div id="links">
          <span>
            {userName} ({houseID}-{houseName}) |{' '}
          </span>
          <a href={`${cfPath()}/login/logout.cfm`}>Log Out</a>
        </div>
      </div>
      <div id="mainMenuFull">
        <div id="mainMenuBar">
          <div className="menu-item">
            <a href={`${cfPath()}/al/landing.cfm`}>Home</a>
          </div>
          <div className="menu-item">
            <div>
              <a href="#nowhere">Settings</a>
            </div>
            <div className="sub-menu">
              {!isInvaluableSeller && (
                <div className="sub-menu-item">
                  <a
                    href={
                      accessSensitive === 1 ? `${cfPath()}/al/settings/geoSettings.cfm` : invalidUrl
                    }
                  >
                    Geographic Bidding Rules
                  </a>
                </div>
              )}
              {!isInvaluableSeller && (
                <div className="sub-menu-item">
                  <a
                    href={
                      accessSensitive === 1
                        ? `${cfPath()}/al/settings/groupSettings.cfm`
                        : invalidUrl
                    }
                  >
                    Auction Terms
                  </a>
                </div>
              )}
              <div className="sub-menu-item">
                <a href={`${cfPath()}/al/contacts/viewContacts.cfm`}>Users and Permissions</a>
              </div>
              <div className="sub-menu-item">
                <Link to={accessSensitive === 1 ? '/settings/locations' : invalidUrl}>
                  Locations
                </Link>
              </div>
              {isInstant && (connectType === 'i' || connectType === 's') && (
                <div className="sub-menu-item">
                  <Link to="/settings/plSettings">Private Label Settings</Link>
                </div>
              )}
            </div>
          </div>
          {!disableINVAInvoicing && (
            <div className="menu-item">
              <a href="#nowhere">Invoices</a>
              <div className="sub-menu">
                <div className="sub-menu-item">
                  <Link to="/invoices/invoiceTemplates">Templates</Link>
                </div>
                <div className="sub-menu-item">
                  <Link to="/invoices">Create New Invoices</Link>
                </div>
                <div className="sub-menu-item">
                  <Link to="/invoices/ExistingInvoices">Manage Existing Invoices</Link>
                </div>
                {showPaymentCenterLink && (
                  <div className="sub-menu-item">
                    <a href={getPaymentCenterLink()}>Payment Center</a>
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="menu-item">
            <a href="#nowhere">Reports</a>
            <div className="sub-menu">
              <div className="sub-menu-item">
                <a href="/ah/reports/sessionResults">Sale Results</a>
              </div>
              {!isInvaluableSeller && (
                <div className="sub-menu-item">
                  <a
                    href={
                      accessReports === 1
                        ? `${cfPath()}/al/reports/viewReportGroups.cfm?mode=stats`
                        : invalidUrl
                    }
                  >
                    Session Statistics
                  </a>
                </div>
              )}
              {showLookerLink && (
                <div className="sub-menu-item">
                  <a href="/ar/campaign-reporting">Ad Campaign Reporting</a>
                </div>
              )}
              {processorType === 'p' && (
                <div className="sub-menu-item">
                  <a href={`${reportsPath}/salesTaxReports`}>Sales Tax Reports</a>
                </div>
              )}
            </div>
          </div>
          <div className="menu-item">
            <a href="#nowhere">Customers</a>
            <div className="sub-menu">
              {!isInvaluableSeller && (
                <div className="sub-menu-item">
                  <a
                    href={
                      accessCustomer === 1
                        ? `${cfPath()}/al/bidders/viewBidderGroups.cfm?mode=approvals`
                        : invalidUrl
                    }
                  >
                    Approvals
                  </a>
                </div>
              )}
              {showYourCustomers && (
                <div className="sub-menu-item">
                  <a href={accessCustomer === 1 ? '/ar/your-customers' : invalidUrl}>
                    Your Customers
                  </a>
                </div>
              )}
              {!isInvaluableSeller && (
                <div className="sub-menu-item">
                  <a
                    href={
                      accessCustomer === 1
                        ? `${cfPath()}/al/bidders/viewBidderControl.cfm?controlType=w`
                        : invalidUrl
                    }
                  >
                    Whitelist
                  </a>
                </div>
              )}
              {!isInvaluableSeller && (
                <div className="sub-menu-item">
                  <a
                    href={
                      accessCustomer === 1
                        ? `${cfPath()}/al/bidders/viewBidderControl.cfm?controlType=b`
                        : invalidUrl
                    }
                  >
                    No Bid List
                  </a>
                </div>
              )}
              <div className="sub-menu-item">
                <a href="/ah/customers/feedback">Feedback</a>
              </div>
              <div className="sub-menu-item">
                <a href="/ah/customers/viewDisputes">Disputes</a>
              </div>
              <div className="sub-menu-item">
                <a href={`${cfPath()}/al/bidders/followers.cfm`}>Followers</a>
              </div>
            </div>
          </div>
          <div className="menu-item">
            {auctionCalendarLink}

            {isSuperUser && (
              <div className="sub-menu">
                <div className="sub-menu-item">
                  <Link to="/my-auctions/snapshots">Catalog Snapshot</Link>
                </div>
              </div>
            )}
          </div>
          <div className="menu-item">
            <a href={`${cfPath()}/al/messaging/viewMessages.cfm`}>Messages</a>
          </div>
          <div className="menu-item">
            <a
              href={
                accessSensitive === 1 ? `${cfPath()}/al/billing/houseInvoiceView.cfm` : invalidUrl
              }
            >
              Billing
            </a>
            <div className="sub-menu">
              <div className="sub-menu-item">
                <a
                  href={
                    accessSensitive === 1
                      ? `${cfPath()}/al/billing/houseCreditCard.cfm`
                      : invalidUrl
                  }
                >
                  Manage Account
                </a>
              </div>
            </div>
          </div>
          <div className="menu-item">
            <a href="#nowhere">Help Center</a>
            <div className="sub-menu">
              {isInvaluableAuctions() ? (
                <>
                  <div className="sub-menu-item">
                    <a
                      href={`${cfPath()}/inv/help-center/`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Invaluable
                    </a>
                  </div>
                  <div className="sub-menu-item">
                    <a
                      href={`${cfPath()}/inv/help-center-az/`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      AuctionZip
                    </a>
                  </div>
                </>
              ) : (
                <div className="sub-menu-item">
                  <a
                    href={`${cfPath()}/inv/help-center/`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    AuctionZip
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
